import { Injectable } from '@angular/core';
import { MaintenanceAndTasksLogsMapped } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import english from '../../../../../i18n/en.json';
import spanish from '../../../../../i18n/es.json';
import portuguese from '../../../../../i18n/pt.json';

@Injectable({
  providedIn: 'root'
})
export class UpdatedByLogService {
  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translateService: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
  }

  public customUpdatedByInLogs(logs: MaintenanceAndTasksLogsMapped[]): MaintenanceAndTasksLogsMapped[] {
    return logs.map((log) => this.generateUpdateByLog(log));
  }

  private generateUpdateByLog (log: MaintenanceAndTasksLogsMapped): MaintenanceAndTasksLogsMapped {
    const logCustomAuthor = {
      type: this.getTypeLog(log),
      updatedBy: this.getUpdatedByLog(log),
      updatedByCompany: this.getUpdatedByCompanyLog(log),
      updatedBySource: this.getUpdatedBySourceLog(log)
    };

    const customAuthorParts = Object.values(logCustomAuthor).filter(Boolean);
    if (customAuthorParts.length >= 2) {
      log.customAuthorDevice = this.getCustomAuthorDeviceLog(log);
      log.customAuthor = customAuthorParts.join(' ');
    }

    return log;
  }

  private getTypeLog(log: MaintenanceAndTasksLogsMapped): string | null {
    if (log.isCreatedLog && (log.status === 'new' || log.snapshot && String(log.snapshot.status) === 'scheduled')) {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.CREATED');
    }

    return this.translateService.instant('MAINTENANCES.TABS.LOGS.UPDATED');
  }

  private getUpdatedBySourceLog(log: MaintenanceAndTasksLogsMapped): string | null {
    if (log.isCreatedLog && log.createdLogBy === 'recurrence') {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.PLATFORMS.SYSTEM_RECURRENCE');
    }

    const isMaintenanceChangedByEnterprise = !log.createdLogBy && log.typeLog === 'maintenance';
    const isTaskChangedByEnterprise = log.typeLog.includes('task') && log.updatedBy && log.updatedBy.type === 'customers';

    if (isMaintenanceChangedByEnterprise || isTaskChangedByEnterprise) {
      let clientApplication = String(log.clientApplicationName || 'BROWSER').toUpperCase();
      if (clientApplication.includes('API')) {
        clientApplication = 'API';
      }

      return this.translateService.instant(`MAINTENANCES.TABS.LOGS.PLATFORMS.${clientApplication}`);
    }

    return null;
  }

  private getUpdatedByCompanyLog(log: MaintenanceAndTasksLogsMapped): string | null {
    const isMaintenance = log.typeLog === 'maintenance';
    const isSammathnaurUser = log.updatedBy && ['sammathnaur-user', 'customers'].includes(log.updatedBy.type);
    const isIntegration = log.updatedBy && log.updatedBy.type && log.updatedBy.type.includes('integration');
    const isEyeofSauronLog = log.createdLogBy === 'eyeofsauron';

    if (log.isCreatedLog && !isSammathnaurUser && isEyeofSauronLog) {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.BY_COMPANY', {
        company: log.company.name
      });
    }

    if (!isMaintenance && !isSammathnaurUser && !isIntegration && log.snapshot && log.snapshot.company) {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.BY_COMPANY', {
        company: log.snapshot.company.name
      });
    }


    if (isMaintenance && !isSammathnaurUser && isEyeofSauronLog && log.status === 'canceled') {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.BY_COMPANY', {
        company: log.company.name
      });
    }

    return null;
  }

  private getUpdatedByLog(log: MaintenanceAndTasksLogsMapped): string | null {
    if (log.updatedBy && log.updatedBy.name) {
      return this.translateService.instant('MAINTENANCES.TABS.LOGS.UPDATED_BY', {
        name: log.updatedBy && log.updatedBy.name
      });
    }

    return null;
  }

  private getCustomAuthorDeviceLog(log: MaintenanceAndTasksLogsMapped): string {
    const customAuthorDeviceMapper = {
      api: 'cloud',
      browser: 'laptop',
      system: 'settings',
      mobile: 'smartphone'
    };

    const device = (log.updatedBy && log.updatedBy['source']) || log.clientSource || log.clientApplicationName;
    if (!device) {
      return customAuthorDeviceMapper['browser'];
    }

    if (device.includes('api') || device.includes('integration')) {
      return customAuthorDeviceMapper['api'];
    }

    return customAuthorDeviceMapper[device];
  }
}
